import { create } from 'zustand';
import type { SubscriptionTierCode, SubscriptionPlan } from '../types';
import { supabase } from '../lib/supabase';
import { stripeProducts } from '../stripe-config';

interface SubscriptionState {
  plans: Record<SubscriptionTierCode, SubscriptionPlan>;
  currentTier: SubscriptionTierCode | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setCurrentTier: (tier: SubscriptionTierCode) => Promise<boolean>;
  upgradeTier: (tier: SubscriptionTierCode) => Promise<boolean>;
  checkProjectLimit: (userId: string) => Promise<boolean>;
  checkSystemSizeLimit: (systemSize: number) => boolean;
  getCurrentPlan: () => SubscriptionPlan | null;
  clearError: () => void;
}

export const subscriptionPlans: Record<SubscriptionTierCode, SubscriptionPlan> = {
  free: {
    tier: 'free',
    maxProjects: 1,
    systemSizeLimit: 20,
    priceMonthly: 0,
    features: [
      '1 project',
      'Up to 20kW system size',
      'Basic support',
      'Standard design tools'
    ]
  },
  business: {
    tier: 'business',
    maxProjects: 5,
    systemSizeLimit: 50,
    priceMonthly: 750,
    features: [
      '5 projects',
      'Up to 50kW system size',
      'Priority support',
      'Advanced design tools',
      'Custom reporting',
      'Team collaboration'
    ]
  },
  enterprise: {
    tier: 'enterprise',
    maxProjects: 10,
    systemSizeLimit: 250,
    priceMonthly: 1500,
    features: [
      '10 projects',
      'Up to 250kW system size',
      'Dedicated support',
      'Advanced analytics',
      'API access',
      'Custom branding',
      'Team management'
    ]
  },
  ultimate: {
    tier: 'ultimate',
    maxProjects: 999999, // Unlimited
    systemSizeLimit: 2000,
    priceMonthly: 2500,
    features: [
      'Unlimited projects',
      'Up to 2000kW system size',
      'White glove support',
      'Premium analytics',
      'Priority processing',
      'Custom integrations',
      'Enterprise SLAs'
    ]
  }
};

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  plans: subscriptionPlans,
  currentTier: 'free', // Default to free tier
  isLoading: false,
  error: null,

  setCurrentTier: async (tier) => {
    set({ isLoading: true, error: null });
    try {
      // Check if user is logged in
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      // If not logged in, just set the tier locally
      if (userError || !user) {
        set({ currentTier: tier });
        return true;
      }

      // If logged in, update the database
      const { error: updateError } = await supabase
        .from('users')
        .update({ subscription_tier: tier })
        .eq('id', user.id);

      if (updateError) {
        console.error('Error updating subscription tier:', updateError);
        throw updateError;
      }

      set({ currentTier: tier });
      return true;
    } catch (error: any) {
      console.error('Error setting subscription tier:', error);
      set({ error: error?.message || 'Failed to set subscription tier' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  upgradeTier: async (tier) => {
    set({ isLoading: true, error: null });
    try {
      // Check if user is logged in
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('Authentication required');

      // If it's the free tier, just update directly
      if (tier === 'free') {
        return await get().setCurrentTier(tier);
      }

      // For paid tiers, create a Stripe checkout session
      const stripeProduct = stripeProducts[tier];
      if (!stripeProduct) {
        throw new Error(`Invalid subscription tier: ${tier}`);
      }

      // Get the current URL for success/cancel redirects
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/subscription?success=true&tier=${tier}`;
      const cancelUrl = `${baseUrl}/subscription?canceled=true`;

      // Create checkout session
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          price_id: stripeProduct.priceId,
          success_url: successUrl,
          cancel_url: cancelUrl,
          mode: 'subscription'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;
      
      return true;
    } catch (error: any) {
      console.error('Error upgrading tier:', error);
      set({ error: error?.message || 'Failed to upgrade subscription' });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  checkProjectLimit: async (userId) => {
    try {
      // Check if user is logged in
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        // If not logged in, assume free tier
        return true;
      }

      const { count, error } = await supabase
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        set({ error: 'Failed to check project limit: ' + error.message });
        return false;
      }

      const { data: userData, error: userError2 } = await supabase
        .from('users')
        .select('subscription_tier')
        .eq('id', userId)
        .single();

      if (userError2) {
        // If can't get user data, assume free tier
        return (count || 0) < subscriptionPlans.free.maxProjects;
      }

      const currentPlan = subscriptionPlans[userData.subscription_tier as SubscriptionTierCode || 'free'];
      return (count || 0) < currentPlan.maxProjects;
    } catch (error) {
      set({ error: 'Failed to check project limit' });
      return false;
    }
  },

  checkSystemSizeLimit: (systemSize) => {
    const currentPlan = get().getCurrentPlan();
    if (!currentPlan) return true; // If no plan is set, don't block
    return systemSize <= currentPlan.systemSizeLimit;
  },

  getCurrentPlan: () => {
    const currentTier = get().currentTier;
    if (!currentTier) return subscriptionPlans.free; // Default to free plan
    return subscriptionPlans[currentTier];
  },

  clearError: () => set({ error: null })
}));